/**
 * Nocwell · Cart upsell (Sleep Set swap)
 *
 * One-tap upgrade: adds the Sleep Set (Deep 250 mask + Quiet earplugs) and removes the
 * standalone mask / earplugs lines it replaces, so the shopper pays the bundle price.
 *
 * Works with the theme's standard cart action (Shopify.actions.updateCart) which re-renders
 * the cart drawer / cart section. Falls back to the AJAX Cart API + reload.
 *
 * Written without private class fields on purpose: the Shopify CDN polyfills theme JS down to
 * ES5 and chokes on `#private` members, which would leave the whole bundle unparsed.
 */
class NcCartUpsell extends HTMLElement {
  constructor() {
    super();
    this.busy = false;
    this.state = { maskColor: '', plugColor: '' };
    this.onClick = this.handleClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);

    // Default selections: keep whatever colour the cart already carries, else first available.
    const variantList = this.variants();
    const firstAvailable = variantList.find((v) => v.available) || variantList[0] || {};
    this.state = {
      maskColor: this.dataset.maskColor || firstAvailable.o1 || '',
      plugColor: this.dataset.plugColor || firstAvailable.o2 || '',
    };

    this.syncSwatches();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
  }

  variants() {
    try {
      const parsed = JSON.parse(this.dataset.variants || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  handleKeydown(event) {
    const swatch = event.target.closest('[data-nc-color]');
    if (!swatch) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      swatch.click();
    }
  }

  handleClick(event) {
    const swatch = event.target.closest('[data-nc-color]');
    if (swatch) {
      event.preventDefault();
      this.state[swatch.dataset.ncColorGroup === 'mask' ? 'maskColor' : 'plugColor'] = swatch.dataset.ncColor;
      this.syncSwatches();
      return;
    }

    const button = event.target.closest('[data-nc-upsell-button]');
    if (button) {
      event.preventDefault();
      this.upgrade(button);
    }
  }

  syncSwatches() {
    this.querySelectorAll('[data-nc-color]').forEach((swatch) => {
      const group = swatch.dataset.ncColorGroup === 'mask' ? 'maskColor' : 'plugColor';
      const active = swatch.dataset.ncColor === this.state[group];
      swatch.setAttribute('aria-pressed', active ? 'true' : 'false');
      swatch.classList.toggle('is-active', active);
      if (active) {
        const nameNode = this.querySelector('[data-nc-color-name="' + swatch.dataset.ncColorGroup + '"]');
        if (nameNode) nameNode.textContent = swatch.dataset.ncColor;
      }
    });

    const selected = this.resolveVariant();
    const button = this.querySelector('[data-nc-upsell-button]');
    if (button) {
      button.disabled = !selected;
      const label = button.querySelector('[data-nc-upsell-label]');
      if (label) label.textContent = selected ? this.dataset.labelReady : this.dataset.labelUnavailable;
    }
    this.clearMessage();
  }

  resolveVariant() {
    const maskColor = this.state.maskColor;
    const plugColor = this.state.plugColor;
    const list = this.variants();
    return (
      list.find((v) => v.o1 === maskColor && v.o2 === plugColor && v.available) ||
      list.find((v) => v.o1 === maskColor && v.o2 === plugColor) ||
      null
    );
  }

  message(text, isError) {
    const node = this.querySelector('[data-nc-upsell-message]');
    if (!node) return;
    node.textContent = text || '';
    node.hidden = !text;
    node.classList.toggle('nc-upsell__message--error', Boolean(isError));
  }

  clearMessage() {
    this.message('', false);
  }

  async upgrade(button) {
    if (this.busy) return;
    const variant = this.resolveVariant();
    if (!variant) return;

    this.busy = true;
    button.classList.add('is-loading');
    this.message(this.dataset.messageWorking, false);

    try {
      const cart = await this.getCart();
      const removeIds = [this.dataset.maskProductId, this.dataset.plugProductId].filter(Boolean);
      const lines = [{ merchandiseId: variant.id, quantity: 1 }];

      for (const item of cart.items || []) {
        if (removeIds.indexOf(String(item.product_id)) !== -1) {
          lines.push({ id: item.key, quantity: 0 });
        }
      }

      let applied = false;

      if (window.Shopify && window.Shopify.actions && window.Shopify.actions.updateCart) {
        try {
          const result = await window.Shopify.actions.updateCart({ lines });
          const errors = (result && result.userErrors) || [];
          if (errors.length) throw new Error(errors[0].message);
          applied = true;
        } catch (error) {
          console.warn('[nc-cart-upsell] standard cart action failed, falling back to the AJAX API:', error);
        }
      }

      if (!applied) await this.ajax(lines, cart);
      // The cart section re-renders from the cart event; the card removes itself once the
      // cart contains the set.
    } catch (error) {
      this.message(this.dataset.messageError || (error && error.message) || 'Something went wrong.', true);
    } finally {
      this.busy = false;
      button.classList.remove('is-loading');
    }
  }

  async getCart() {
    const base = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
    const response = await fetch(base + '.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not read the cart.');
    return response.json();
  }

  /** Fallback for when the standard cart action is not on the page. */
  async ajax(lines, cart) {
    const routes = (window.Theme && window.Theme.routes) || {};
    const add = lines.find((line) => line.merchandiseId);
    await fetch((routes.cart_add_url || '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: add.merchandiseId, quantity: add.quantity }] }),
    });

    for (const item of cart.items || []) {
      const shouldRemove = lines.some((line) => line.id === item.key);
      if (!shouldRemove) continue;
      await fetch((routes.cart_change_url || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      });
    }
    window.location.reload();
  }
}

if (!customElements.get('nc-cart-upsell')) {
  customElements.define('nc-cart-upsell', NcCartUpsell);
}
